import type { OrderStatus } from "../../../components/common/chip";

interface SaleLike {
  state?: string | null;
  invoiceStatus?: string | null;
  buyerReceived?: boolean | null;
  /** Delivery status from Odoo: "no" | "pending" | "partial" | "full" */
  deliveryStatus?: string | null;
  /** True once the seller confirms shipping details on the related PO */
  sellerShipped?: boolean | null;
  /** Dropshipping records from the detail view. */
  dropshipping?: { id: number; state?: string | null }[] | null;
}

/**
 * ═══════════════════════════════════════════════════════════════════════
 * CRITICAL: Order Status Flow — DO NOT CHANGE without product confirmation.
 * ═══════════════════════════════════════════════════════════════════════
 *
 * Buyer sale-order view:  processing → inTransit → delivered → validated
 *
 *  - "processing": SO confirmed, seller has NOT shipped yet.
 *  - "inTransit":  Seller clicked "Mark as Shipped" on the PO, which sets
 *                  sale.order.seller_shipped = true via the backend.
 *                  This is the ONLY trigger — NOT picking state, NOT
 *                  delivery_status="pending", NOT the existence of pickings.
 *  - "delivered":  Seller clicked "Mark as Delivered" (picking state=done,
 *                  delivery_status=full, or invoiceStatus=invoiced).
 *  - "validated":  Buyer clicked "Mark as Received" (buyerReceived=true).
 *
 * IMPORTANT:
 *   Odoo sets delivery_status="pending" and picking state="assigned" the
 *   moment the SO is confirmed (before seller acts). Using those fields
 *   for "inTransit" causes orders to skip straight to "In Transit" on
 *   purchase. The explicit `sellerShipped` boolean is the only safe signal.
 * ═══════════════════════════════════════════════════════════════════════
 */
export function deriveOrderStatus(sale: SaleLike): OrderStatus {
  const state = (sale.state ?? "").toLowerCase();
  if (state === "cancel") return "cancelled";

  if (sale.buyerReceived) return "validated";

  const ds = (sale.deliveryStatus ?? "").toLowerCase();
  if (ds === "full" || ds === "partial") return "delivered";

  if (state === "done") return "delivered";

  // invoiceStatus === "invoiced" is NOT a delivery signal — the invoice is
  // now created at Stripe payment time, so it's "invoiced" immediately.

  const pickings = sale.dropshipping ?? [];
  const allDone = pickings.length > 0 && pickings.every((d) => d.state === "done");
  if (allDone) return "delivered";

  // DO NOT replace this with picking state or delivery_status checks — see above.
  if (sale.sellerShipped) return "inTransit";

  return "processing";
}

/** The goods have reached the buyer — warranty claims open from here on. */
export function isOrderDelivered(status: OrderStatus): boolean {
  return status === "delivered" || status === "validated";
}

/** The seller has shipped — the buyer can report an issue with the order from here on. */
export function hasOrderShipped(status: OrderStatus): boolean {
  return status === "inTransit" || isOrderDelivered(status);
}

export function isQuotationState(state: string | null | undefined): boolean {
  const s = (state ?? "").toLowerCase();
  return s === "draft" || s === "sent";
}

export function formatLongDate(value: string | null | undefined): string {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

export function formatDateTime(value: string | null | undefined): string {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}
